import { Injectable } from '@angular/core';
import {
  CRM_API,
  CreateCRMUserDto,
  ICoupon,
  ICRMUser,
  ICRMUserTokenPayload,
  ISchedule,
  IWorkflowRun,
  PageBatchResponse
} from '@care-portals/api-interfaces';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { Observable } from 'rxjs';

@Injectable()
export class CRMService {
  BASE_URL = environment.auth.baseUrl;

  constructor(public http: HttpClient) { }

  getSchedule() {
    return this.http.get<ISchedule>(this.BASE_URL + '/users/schedule');
  }

  updateSchedule(updates: Partial<ISchedule>) {
    return this.http.put<ISchedule>(this.BASE_URL + '/users/schedule', updates);
  }

  ///////////////////////
  ///  ADMIN Users
  //////////////////////

  getAdminUsers(): Observable<ICRMUserTokenPayload> {
    try {
      return this.http.get<ICRMUserTokenPayload>(
        this.BASE_URL + CRM_API.users.base + CRM_API.users.getCurrent
      );
    } catch (error) {
      console.error(error);
      throw new Error('Failed to get user: ' + error);
    }
  }

  createAdminUser(body: CreateCRMUserDto) {
    return this.http.post<ICRMUser>(this.BASE_URL + CRM_API.users.base, body);
  }

  updateAdminUser(body: ICRMUser) {
    return this.http.put<ICRMUser>(this.BASE_URL + CRM_API.users.base, body);
  }

  deleteAdminUser(_id: string) {
    return this.http.delete<{ crmUserId: string }>(
      this.BASE_URL + `${CRM_API.users.base}/${_id}`
    );
  }

  resetAdminUserPassword(_id: string) {
    return this.http.post(
      this.BASE_URL + CRM_API.users.base + CRM_API.users.password,
      { _id }
    );
  }

  syncSchedule(id: string) {
    return this.http.get<ISchedule>(
      this.BASE_URL +
      CRM_API.schedules.base +
      CRM_API.schedules.sync.replace(':id', id)
    );
  }

  getCoupons(filters: { affectedProducts?: string } = {}): Observable<PageBatchResponse<ICoupon>> {
    return this.http.get<PageBatchResponse<ICoupon>>(
      this.BASE_URL + CRM_API.coupons.base,
      { params: { size: '1000', ...filters } }
    );
  }

  /** Fetch a single workflow run (full detail) via the CRM API. */
  getWorkflowRun(id: string): Observable<IWorkflowRun> {
    return this.http.get<IWorkflowRun>(
      this.BASE_URL + CRM_API.workflowRuns.base + `/${id}`
    );
  }

  /**
   * Rerun a failed workflow run via the CRM API. EMR has no rerun endpoint of
   * its own. It reuses the existing CRM one (same JWT/org context, see
   * environment.auth.baseUrl). Lets EMR users retry without going to the CRM.
   */
  rerunWorkflowRun(id: string): Observable<IWorkflowRun> {
    return this.http.put<IWorkflowRun>(
      this.BASE_URL + CRM_API.workflowRuns.base + `/${id}/rerun`,
      {}
    );
  }
}
